import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import { 
  Users, TrendingUp, TrendingDown, DollarSign, Package, Building2, UserCheck,
  ChevronDown, ChevronRight, Download, RefreshCw, Calendar, Printer, FileText,
  ArrowUpRight, ArrowDownRight, Minus, Filter, BarChart3, PieChart as PieChartIcon,
  Target, Percent, Globe, Layers, Award, Clock, CalendarRange, FileSpreadsheet
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palettes
const LEVEL_COLORS = {
  partner: ["#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe"],
  dpClient: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#d1fae5"],
  agent: ["#f59e0b", "#fbbf24", "#fcd34d", "#fde68a", "#fef3c7"],
  manager: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe", "#ede9fe"]
};

const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

// Date range presets
const DATE_PRESETS = {
  thisMonth: { label: "This Month", getValue: () => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, quarter: null };
  }},
  lastMonth: { label: "Last Month", getValue: () => {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { year: last.getFullYear(), month: last.getMonth() + 1, quarter: null };
  }},
  thisQuarter: { label: "This Quarter", getValue: () => {
    const now = new Date();
    const quarter = Math.ceil((now.getMonth() + 1) / 3);
    return { year: now.getFullYear(), month: null, quarter };
  }},
  lastQuarter: { label: "Last Quarter", getValue: () => {
    const now = new Date();
    let quarter = Math.ceil((now.getMonth() + 1) / 3) - 1;
    let year = now.getFullYear();
    if (quarter === 0) { quarter = 4; year--; }
    return { year, month: null, quarter };
  }},
  thisYear: { label: "This Year", getValue: () => {
    return { year: new Date().getFullYear(), month: null, quarter: null };
  }},
  lastYear: { label: "Last Year", getValue: () => {
    return { year: new Date().getFullYear() - 1, month: null, quarter: null };
  }}
};

const PerformanceReportsPage = () => {
  const { token } = useAuth();
  const printRef = useRef();
  
  // Tab state
  const [activeTab, setActiveTab] = useState("partner");
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Data
  const [reportData, setReportData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  
  // Date filters
  const [dateMode, setDateMode] = useState("monthly"); // monthly, quarterly, yearly, custom
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [latestMonthApplied, setLatestMonthApplied] = useState(false);
  
  // Comparison
  const [comparisonMode, setComparisonMode] = useState("none"); // none, mom, yoy
  const [compareYear, setCompareYear] = useState(null);
  const [compareMonth, setCompareMonth] = useState(null);
  const [compareQuarter, setCompareQuarter] = useState(null);
  
  // Drill-down
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Chart preferences
  const [chartType, setChartType] = useState("bar");

  // Monthly Trend (team performance meeting view)
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendMetric, setTrendMetric] = useState("sales"); // sales | gp | payments
  const [trendCompareMode, setTrendCompareMode] = useState("peak"); // peak | mom
  const [trendShowExportDialog, setTrendShowExportDialog] = useState(false);
  const [trendExportPicks, setTrendExportPicks] = useState({ sales: true, gp: true, payments: true });
  // Trend scope filter — "company" or "<level>:<code>" e.g. "partner:RAM" / "manager:M-1"
  const [trendScope, setTrendScope] = useState("company");
  const [allLedgers, setAllLedgers] = useState([]);
  const [trendCodeSearch, setTrendCodeSearch] = useState("");

  // Top Movers (latest month MoM)
  const [topMovers, setTopMovers] = useState(null);
  const [topMoversLoading, setTopMoversLoading] = useState(false);
  const [topMoversMonth, setTopMoversMonth] = useState(new Date().getMonth() + 1);
  
  // Generate year/month/quarter options
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
  }, []);
  
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" },
    { value: 3, label: "March" }, { value: 4, label: "April" },
    { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" },
    { value: 9, label: "September" }, { value: 10, label: "October" },
    { value: 11, label: "November" }, { value: 12, label: "December" }
  ];
  
  const quarters = [
    { value: 1, label: "Q1 (Jan-Mar)" },
    { value: 2, label: "Q2 (Apr-Jun)" },
    { value: 3, label: "Q3 (Jul-Sep)" },
    { value: 4, label: "Q4 (Oct-Dec)" }
  ];

  // Calculate comparison period based on mode
  useEffect(() => {
    if (comparisonMode === "mom") {
      // Month-over-Month
      let prevMonth = selectedMonth - 1;
      let prevYear = selectedYear;
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear--;
      }
      setCompareMonth(prevMonth);
      setCompareYear(prevYear);
      setCompareQuarter(null);
    } else if (comparisonMode === "yoy") {
      // Year-over-Year
      setCompareYear(selectedYear - 1);
      // In yearly mode, compare full year (no specific month/quarter)
      if (dateMode === "yearly") {
        setCompareMonth(null);
        setCompareQuarter(null);
      } else {
        setCompareMonth(selectedMonth);
        setCompareQuarter(selectedQuarter);
      }
    } else {
      setCompareYear(null);
      setCompareMonth(null);
      setCompareQuarter(null);
    }
  }, [comparisonMode, selectedYear, selectedMonth, selectedQuarter, dateMode]);

  // Fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append("level", activeTab);
      params.append("year", selectedYear);
      
      if (dateMode === "monthly") {
        params.append("month", selectedMonth);
      } else if (dateMode === "quarterly") {
        params.append("quarter", selectedQuarter);
      } else if (dateMode === "custom" && customDateFrom && customDateTo) {
        params.append("date_from", customDateFrom);
        params.append("date_to", customDateTo);
      }
      
      if (comparisonMode !== "none" && compareYear) {
        params.append("compare_year", compareYear);
        if (compareMonth) params.append("compare_month", compareMonth);
        if (compareQuarter) params.append("compare_quarter", compareQuarter);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/reports/performance?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data.current);
        setComparisonData(data.comparison);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [activeTab, dateMode, selectedYear, selectedMonth, selectedQuarter, customDateFrom, customDateTo, comparisonMode]);

  // On first mount: default the filter to the last month with data, so the
  // summary cards aren't empty when the current month has no rows yet.
  useEffect(() => {
    if (latestMonthApplied || !token) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/reports/performance/latest-month`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const { year, month } = await r.json();
        if (year && month) {
          setSelectedYear(year);
          setSelectedMonth(month);
          setSelectedQuarter(Math.ceil(month / 3));
        }
      } catch (e) {
        // silent — fall back to current-month defaults
      } finally {
        setLatestMonthApplied(true);
      }
    })();
  }, [token, latestMonthApplied]);

  // Monthly Matrix — rows = clients at current level, cols = months of the year
  const [matrixMetric, setMatrixMetric] = useState("revenue"); // revenue | orders | gp
  const [matrixData, setMatrixData] = useState(null);
  const [matrixPrevData, setMatrixPrevData] = useState(null); // prior year for compare
  const [matrixComparePrev, setMatrixComparePrev] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);

  const fetchMatrix = async () => {
    setMatrixLoading(true);
    try {
      const url = `${BACKEND_URL}/api/reports/performance/monthly-matrix?level=${activeTab}&year=${selectedYear}`;
      const [rCur, rPrev] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        matrixComparePrev
          ? fetch(`${BACKEND_URL}/api/reports/performance/monthly-matrix?level=${activeTab}&year=${selectedYear - 1}`, { headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve(null),
      ]);
      if (rCur && rCur.ok) setMatrixData(await rCur.json());
      if (rPrev && rPrev.ok) setMatrixPrevData(await rPrev.json());
      else if (!matrixComparePrev) setMatrixPrevData(null);
    } catch (e) {
      /* silent */
    } finally {
      setMatrixLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedYear, token, matrixComparePrev]);

  // Build a fast lookup of prior year values keyed by client code
  const prevByCode = useMemo(() => {
    const map = {};
    (matrixPrevData?.rows || []).forEach((r) => { map[r.code] = r; });
    return map;
  }, [matrixPrevData]);

  const deltaPct = (cur, prev) => {
    if (prev === 0 || prev == null) return cur > 0 ? 100 : (cur < 0 ? -100 : 0);
    return ((cur - prev) / Math.abs(prev)) * 100;
  };

  // Top movers — biggest gainers/droppers by absolute delta (₹ or count) of the
  // selected metric. Only meaningful when compare-toggle is ON.
  const matrixTopMovers = useMemo(() => {
    if (!matrixComparePrev || !matrixData?.rows?.length) return null;
    const rows = matrixData.rows.map((r) => {
      const cur = r.total?.[matrixMetric] || 0;
      const prev = prevByCode[r.code]?.total?.[matrixMetric] || 0;
      return { code: r.code, name: r.name || r.code, cur, prev, deltaAbs: cur - prev, deltaPct: deltaPct(cur, prev) };
    }).filter((x) => x.cur !== 0 || x.prev !== 0);
    const gainers = [...rows].filter((x) => x.deltaAbs > 0).sort((a, b) => b.deltaAbs - a.deltaAbs).slice(0, 3);
    const droppers = [...rows].filter((x) => x.deltaAbs < 0).sort((a, b) => a.deltaAbs - b.deltaAbs).slice(0, 3);
    return { gainers, droppers };
  }, [matrixData, matrixPrevData, matrixComparePrev, matrixMetric, prevByCode]);

  // Export the current matrix (all rows × 12 months) to Excel
  const exportMatrixExcel = () => {
    if (!matrixData || (matrixData.rows || []).length === 0) return;
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const buildSheet = (metric) => {
      const rows = matrixData.rows.map((r) => {
        const row = { Code: r.code, Name: r.name || r.code };
        for (let m = 1; m <= 12; m++) {
          const cell = r.months[m] || r.months[String(m)] || { orders: 0, revenue: 0, gp: 0 };
          row[monthNames[m - 1]] = cell[metric] || 0;
        }
        row.Total = r.total?.[metric] || 0;
        return row;
      });
      // Grand total
      const grand = { Code: "TOTAL", Name: "" };
      for (let m = 1; m <= 12; m++) {
        const gm = matrixData.grand_total?.months?.[m] || matrixData.grand_total?.months?.[String(m)] || {};
        grand[monthNames[m - 1]] = gm[metric] || 0;
      }
      grand.Total = matrixData.grand_total?.[metric] || 0;
      rows.push(grand);
      return XLSX.utils.json_to_sheet(rows);
    };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildSheet("orders"), "Orders");
    XLSX.utils.book_append_sheet(wb, buildSheet("revenue"), "Revenue");
    XLSX.utils.book_append_sheet(wb, buildSheet("gp"), "GP");
    const levelLabel = activeTab === "partner" ? "Partners" : activeTab === "dpClient" ? "DPClients" : activeTab === "agent" ? "Agents" : "Managers";
    XLSX.writeFile(wb, `Monthly_Matrix_${levelLabel}_${selectedYear}.xlsx`);
    toast.success("Excel exported");
  };

  // Fetch Monthly Trend
  const fetchMonthlyTrend = async () => {
    setTrendLoading(true);
    try {
      const params = new URLSearchParams({ year: String(trendYear) });
      if (trendScope && trendScope !== "company") {
        const [kind, code] = trendScope.split(":");
        if (code) {
          if (kind === "manager") params.append("manager_code", code);
          else params.append("client_code", code);
        }
      }
      const response = await fetch(`${BACKEND_URL}/api/reports/monthly-trend?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrendData(data);
      }
    } catch (error) {
      console.error("monthly-trend fetch error", error);
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendYear, trendScope]);

  // Fetch ledgers list once for the trend scope dropdown
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/ledgers`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          if (alive) setAllLedgers(d.ledgers || []);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top Movers fetch — based on selected metric tab + month
  const fetchTopMovers = async () => {
    setTopMoversLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/reports/top-movers?year=${trendYear}&month=${topMoversMonth}&metric=${trendMetric}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) setTopMovers(await r.json());
    } catch (e) { /* ignore */ }
    finally { setTopMoversLoading(false); }
  };

  useEffect(() => {
    fetchTopMovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendYear, topMoversMonth, trendMetric]);

  // Toggle item expansion
  const toggleItem = (code) => {
    setExpandedItems(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
    
    // Scroll to drill-down section
    if (!expandedItems[code]) {
      setTimeout(() => {
        const element = document.getElementById(`drilldown-${code}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Calculate change percentage
  const calcChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!reportData?.items) return;
    setExporting(true);
    
    try {
      const worksheetData = reportData.items.map(item => ({
        [activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager"]: item.name || item.code,
        "Orders": item.orders,
        "Quantity": item.quantity,
        "Revenue": item.revenue,
        "Cost": item.cost,
        "Gross Profit": item.gross_profit,
        "GP %": item.gp_percentage?.toFixed(1) + "%",
        "Avg Order Value": item.avg_order_value,
        ...(comparisonData ? {
          "Prev Orders": comparisonData.items?.find(c => c.code === item.code)?.orders || 0,
          "Prev Revenue": comparisonData.items?.find(c => c.code === item.code)?.revenue || 0,
          "Revenue Change %": calcChange(item.revenue, comparisonData.items?.find(c => c.code === item.code)?.revenue) || "N/A"
        } : {})
      }));
      
      const ws = XLSX.utils.json_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${activeTab} Report`);
      
      // Add summary sheet
      const summaryData = [{
        "Total Orders": reportData.summary?.total_orders,
        "Total Revenue": reportData.summary?.total_revenue,
        "Total GP": reportData.summary?.total_gp,
        "Avg GP %": reportData.summary?.avg_gp_percentage?.toFixed(1) + "%"
      }];
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
      
      const fileName = `${activeTab}_performance_${selectedYear}_${selectedMonth || selectedQuarter || 'full'}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel exported successfully!");
    } catch (error) {
      toast.error("Failed to export Excel");
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!reportData?.items) return;
    setExporting(true);
    
    try {
      const doc = new jsPDF();
      const levelName = activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager";
      
      // Title
      doc.setFontSize(18);
      doc.text(`${levelName} Performance Report`, 14, 22);
      
      // Period
      doc.setFontSize(11);
      let periodText = `Period: ${selectedYear}`;
      if (dateMode === "monthly") periodText += ` - ${months.find(m => m.value === selectedMonth)?.label}`;
      else if (dateMode === "quarterly") periodText += ` - ${quarters.find(q => q.value === selectedQuarter)?.label}`;
      doc.text(periodText, 14, 30);
      
      // Summary
      doc.setFontSize(12);
      doc.text(`Total Orders: ${reportData.summary?.total_orders?.toLocaleString()}`, 14, 40);
      doc.text(`Total Revenue: ${formatCurrency(reportData.summary?.total_revenue)}`, 14, 48);
      doc.text(`Total GP: ${formatCurrency(reportData.summary?.total_gp)}`, 100, 40);
      doc.text(`Avg GP %: ${reportData.summary?.avg_gp_percentage?.toFixed(1)}%`, 100, 48);
      
      // Table
      const tableData = reportData.items.map(item => [
        item.name || item.code,
        item.orders?.toLocaleString(),
        formatCurrency(item.revenue),
        formatCurrency(item.gross_profit),
        `${item.gp_percentage?.toFixed(1)}%`
      ]);
      
      doc.autoTable({
        startY: 55,
        head: [[levelName, 'Orders', 'Revenue', 'Gross Profit', 'GP %']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] }
      });
      
      const fileName = `${activeTab}_performance_${selectedYear}_${selectedMonth || selectedQuarter || 'full'}.pdf`;
      doc.save(fileName);
      toast.success("PDF exported successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  // Get level icon
  const getLevelIcon = (level) => {
    switch (level) {
      case "partner": return Building2;
      case "dpClient": return Layers;
      case "agent": return UserCheck;
      case "manager": return Users;
      default: return Users;
    }
  };

  // Get level color
  const getLevelColor = (level) => {
    switch (level) {
      case "partner": return "from-blue-500 to-blue-600";
      case "dpClient": return "from-emerald-500 to-emerald-600";
      case "agent": return "from-amber-500 to-amber-600";
      case "manager": return "from-violet-500 to-violet-600";
      default: return "from-gray-500 to-gray-600";
    }
  };

  const LevelIcon = getLevelIcon(activeTab);

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen print:bg-white print:p-2" ref={printRef}>
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Performance Reports
          </h1>
          <p className="text-slate-500 text-sm">Sales, GP & Comparison at Partner, DP Client, Agent & Manager levels</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={exportToPDF} disabled={exporting}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel} disabled={exporting}>
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={fetchReportData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold">
          {activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager"} Performance Report
        </h1>
        <p className="text-sm text-gray-600">
          Period: {selectedYear} {dateMode === "monthly" ? `- ${months.find(m => m.value === selectedMonth)?.label}` : dateMode === "quarterly" ? `- ${quarters.find(q => q.value === selectedQuarter)?.label}` : ""}
        </p>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Date Mode */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Period Type</Label>
              <Select value={dateMode} onValueChange={setDateMode}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Year */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Month (if monthly) */}
            {dateMode === "monthly" && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Month</Label>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Quarter (if quarterly) */}
            {dateMode === "quarterly" && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Quarter</Label>
                <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(Number(v))}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {quarters.map(q => (
                      <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Custom Date Range */}
            {dateMode === "custom" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">From</Label>
                  <DMYDateInput value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} className="w-36" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">To</Label>
                  <DMYDateInput value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} className="w-36" />
                </div>
              </>
            )}
            
            {/* Comparison Mode */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Comparison</Label>
              <Select value={comparisonMode} onValueChange={setComparisonMode}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Comparison</SelectItem>
                  <SelectItem value="mom">Month-over-Month</SelectItem>
                  <SelectItem value="yoy">Year-over-Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Chart Type */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Chart</Label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="pie">Pie</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend Card (Team Performance Meeting view) */}
      <Card className="border-slate-200" data-testid="monthly-trend-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarRange className="w-4 h-4 text-blue-600" />
                Monthly Trend — {trendYear}
                {trendScope !== "company" && trendData?.scope && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                    {trendData.scope.manager_code ? `Manager: ${trendData.scope.manager_code}` : `Code: ${trendData.scope.client_code}`}
                  </span>
                )}
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                {trendScope === "company"
                  ? "Company-wide month-by-month parcels, GP and payment collections."
                  : "Filtered view — month-by-month performance for the selected scope."}
                {" "}Each month is benchmarked against the highest-performing month.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Scope filter */}
              <Select value={trendScope} onValueChange={(v) => { setTrendScope(v); setTrendCodeSearch(""); }}>
                <SelectTrigger className="w-56 h-9" data-testid="trend-scope-select">
                  <SelectValue placeholder="Filter scope" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {/* Search input pinned at top */}
                  <div className="px-2 py-1.5 sticky top-0 bg-white z-10 border-b border-slate-100">
                    <Input
                      autoFocus
                      placeholder="Search code or name..."
                      value={trendCodeSearch}
                      onChange={(e) => setTrendCodeSearch(e.target.value)}
                      onKeyDown={(e) => { e.stopPropagation(); }}
                      className="h-8 text-xs"
                      data-testid="trend-scope-search"
                    />
                  </div>
                  <SelectItem value="company">🏢 Company-wide (all)</SelectItem>
                  {(() => {
                    const q = (trendCodeSearch || "").trim().toLowerCase();
                    const matches = (l) => {
                      if (!q) return true;
                      return (l.client_code || "").toLowerCase().includes(q) || (l.name || "").toLowerCase().includes(q);
                    };
                    const groups = [
                      { label: "Partners", role: "partner", icon: "🏬" },
                      { label: "DP Clients", role: "client", icon: "📦" },
                      { label: "DP Clients (alt)", role: "dp client", icon: "📦" },
                      { label: "Agents", role: "agent", icon: "🧑‍💼" },
                    ];
                    const out = [];
                    groups.forEach(g => {
                      const items = allLedgers.filter(l => (l.role || "").toLowerCase() === g.role && matches(l));
                      if (items.length === 0) return;
                      out.push(<div key={`hdr-${g.label}`} className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{g.icon} {g.label} ({items.length})</div>);
                      items.slice(0, 200).forEach(l => {
                        out.push(
                          <SelectItem key={`${g.role}:${l.client_code}`} value={`${g.role === "client" || g.role === "dp client" ? "client" : g.role}:${l.client_code}`} className="text-sm">
                            <span className="font-mono text-xs text-blue-700">{l.client_code}</span> {l.name && l.name !== l.client_code ? <span className="text-slate-600">— {l.name}</span> : null}
                          </SelectItem>
                        );
                      });
                    });
                    // Managers — derived from `under` field
                    const mgrSet = Array.from(new Set(allLedgers.map(l => (l.under || "").toUpperCase().trim()).filter(Boolean)));
                    const mgrFiltered = mgrSet.filter(m => !q || m.toLowerCase().includes(q));
                    if (mgrFiltered.length > 0) {
                      out.push(<div key="hdr-mgr" className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">👥 Managers ({mgrFiltered.length})</div>);
                      mgrFiltered.forEach(m => {
                        out.push(
                          <SelectItem key={`manager:${m}`} value={`manager:${m}`} className="text-sm">
                            <span className="font-mono text-xs text-purple-700">{m}</span> <span className="text-slate-500 text-xs">(team)</span>
                          </SelectItem>
                        );
                      });
                    }
                    return out;
                  })()}
                </SelectContent>
              </Select>
              <Select value={String(trendYear)} onValueChange={(v) => setTrendYear(Number(v))}>
                <SelectTrigger className="w-28 h-9" data-testid="trend-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchMonthlyTrend} disabled={trendLoading} data-testid="trend-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${trendLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTrendShowExportDialog(true)}
                disabled={!trendData}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="trend-export-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Export Excel
              </Button>
            </div>
          </div>
          {/* Sub-tabs */}
          <div className="flex flex-wrap items-center gap-2 pt-3">
            {[
              { key: "sales", label: "Sales (Parcels & Revenue)" },
              { key: "gp", label: "Gross Profit" },
              { key: "payments", label: "Payment Collection" },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTrendMetric(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${trendMetric === t.key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`trend-metric-${t.key}`}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 text-xs">
              <span className="text-slate-500">Compare to:</span>
              <div className="inline-flex rounded-full border border-slate-300 overflow-hidden">
                {[
                  { key: "peak", label: "Peak Month" },
                  { key: "mom", label: "Previous Month" },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setTrendCompareMode(opt.key)}
                    className={`px-3 py-1 font-semibold transition-colors ${trendCompareMode === opt.key ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`trend-compare-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Top Movers Strip */}
          {trendScope === "company" && topMovers && (topMovers.risers.length > 0 || topMovers.fallers.length > 0 || topMovers.new_entrants.length > 0) && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3" data-testid="top-movers-strip">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-bold text-slate-700">Top Movers</span>
                  <span className="text-xs text-slate-500">
                    {topMovers.current_label} vs {topMovers.previous_label} •{" "}
                    {trendMetric === "sales" ? "Parcels" : trendMetric === "gp" ? "Gross Profit" : "Payments Collected"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs border-slate-300 hover:bg-slate-100"
                    data-testid="top-movers-copy-btn"
                    onClick={() => {
                      const fmtUnit = trendMetric === "sales" ? "" : "₹";
                      const fmtVal = (v) => trendMetric === "sales" ? Number(v).toLocaleString() : `₹${Math.round(v).toLocaleString()}`;
                      const lines = [];
                      const metricLabel = trendMetric === "sales" ? "Parcels" : trendMetric === "gp" ? "Gross Profit" : "Payments";
                      lines.push(`📊 EximFlow Top Movers — ${topMovers.current_label} vs ${topMovers.previous_label} (${metricLabel})`);
                      lines.push("");
                      if (topMovers.risers.length) {
                        lines.push("🟢 Top Risers");
                        topMovers.risers.forEach(m => {
                          lines.push(`  • ${m.client_code} ▲ ${m.pct.toFixed(1)}%  (${fmtVal(m.previous)} → ${fmtVal(m.current)})`);
                        });
                        lines.push("");
                      }
                      if (topMovers.fallers.length) {
                        lines.push("🔴 Top Fallers");
                        topMovers.fallers.forEach(m => {
                          lines.push(`  • ${m.client_code} ▼ ${Math.abs(m.pct).toFixed(1)}%  (${fmtVal(m.previous)} → ${fmtVal(m.current)})`);
                        });
                        lines.push("");
                      }
                      if (topMovers.new_entrants.length) {
                        lines.push("🔵 New / Returning");
                        topMovers.new_entrants.forEach(m => {
                          lines.push(`  • ${m.client_code} — ${fmtVal(m.current)} (prev: 0)`);
                        });
                      }
                      const text = lines.join("\n").replace(/₹/g, fmtUnit ? "₹" : "");
                      try {
                        navigator.clipboard.writeText(text);
                        toast.success("Top Movers summary copied — ready to paste in WhatsApp/Slack");
                      } catch (e) {
                        // Fallback
                        const ta = document.createElement("textarea");
                        ta.value = text;
                        document.body.appendChild(ta);
                        ta.select();
                        try { document.execCommand("copy"); toast.success("Copied!"); }
                        catch (_) { toast.error("Copy failed — please select & copy manually"); }
                        document.body.removeChild(ta);
                      }
                    }}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" /> Copy Summary
                  </Button>
                  <span className="text-xs text-slate-500">Month:</span>
                  <Select value={String(topMoversMonth)} onValueChange={(v) => setTopMoversMonth(Number(v))}>
                    <SelectTrigger className="w-32 h-8" data-testid="top-movers-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (<SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {topMoversLoading ? (
                <div className="text-xs text-slate-400 py-3">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Risers */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1.5 flex items-center gap-1">
                      <ArrowUpRight className="w-3 h-3" /> Top Risers
                    </div>
                    <div className="space-y-1.5">
                      {topMovers.risers.length === 0 && <div className="text-xs text-slate-400 italic">No risers</div>}
                      {topMovers.risers.map((m) => (
                        <button
                          key={`r-${m.client_code}`}
                          onClick={() => {
                            const kind = (m.role === "client" || m.role === "dp client") ? "client" : m.role;
                            setTrendScope(`${kind}:${m.client_code}`);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="w-full flex items-center justify-between px-2.5 py-1.5 rounded border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 transition-colors text-left"
                          data-testid={`top-mover-riser-${m.client_code}`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-bold text-emerald-800 font-mono truncate">{m.client_code}</span>
                            <span className="text-[10px] text-slate-500 truncate">{m.name}</span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-emerald-700">▲ {m.pct.toFixed(1)}%</span>
                            <span className="text-[10px] text-slate-500">{m.previous.toLocaleString()} → {m.current.toLocaleString()}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Fallers */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-red-600 mb-1.5 flex items-center gap-1">
                      <ArrowDownRight className="w-3 h-3" /> Top Fallers
                    </div>
                    <div className="space-y-1.5">
                      {topMovers.fallers.length === 0 && <div className="text-xs text-slate-400 italic">No fallers</div>}
                      {topMovers.fallers.map((m) => (
                        <button
                          key={`f-${m.client_code}`}
                          onClick={() => {
                            const kind = (m.role === "client" || m.role === "dp client") ? "client" : m.role;
                            setTrendScope(`${kind}:${m.client_code}`);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="w-full flex items-center justify-between px-2.5 py-1.5 rounded border border-red-200 bg-red-50/60 hover:bg-red-100 transition-colors text-left"
                          data-testid={`top-mover-faller-${m.client_code}`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-bold text-red-800 font-mono truncate">{m.client_code}</span>
                            <span className="text-[10px] text-slate-500 truncate">{m.name}</span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-red-700">▼ {Math.abs(m.pct).toFixed(1)}%</span>
                            <span className="text-[10px] text-slate-500">{m.previous.toLocaleString()} → {m.current.toLocaleString()}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* New Entrants */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-1.5 flex items-center gap-1">
                      <Award className="w-3 h-3" /> New / Returning
                    </div>
                    <div className="space-y-1.5">
                      {topMovers.new_entrants.length === 0 && <div className="text-xs text-slate-400 italic">None this month</div>}
                      {topMovers.new_entrants.map((m) => (
                        <button
                          key={`n-${m.client_code}`}
                          onClick={() => {
                            const kind = (m.role === "client" || m.role === "dp client") ? "client" : m.role;
                            setTrendScope(`${kind}:${m.client_code}`);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="w-full flex items-center justify-between px-2.5 py-1.5 rounded border border-blue-200 bg-blue-50/60 hover:bg-blue-100 transition-colors text-left"
                          data-testid={`top-mover-new-${m.client_code}`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-bold text-blue-800 font-mono truncate">{m.client_code}</span>
                            <span className="text-[10px] text-slate-500 truncate">{m.name}</span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-blue-700">{m.current.toLocaleString()}</span>
                            <span className="text-[10px] text-slate-500">prev: 0</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-2 text-[10px] text-slate-500">Click any card to deep-dive into that partner's monthly trend.</div>
            </div>
          )}

          {/* Trend table follows */}
          {trendLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <RefreshCw className="w-6 h-6 mr-2 animate-spin" /> Loading trend...
            </div>
          ) : !trendData?.months ? (
            <div className="text-center py-10 text-slate-400 text-sm">No trend data</div>
          ) : (() => {
            const months = trendData.months;
            // Define column groups per metric
            const cols = trendMetric === "sales"
              ? [
                  { key: "orders", label: "Parcels", fmt: (v) => v.toLocaleString(), positiveBetter: true },
                  { key: "quantity", label: "Quantity", fmt: (v) => v.toLocaleString(), positiveBetter: true },
                  { key: "revenue", label: "Revenue", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: true },
                ]
              : trendMetric === "gp"
              ? [
                  { key: "revenue", label: "Revenue", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: true },
                  { key: "cost", label: "Cost", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: false },
                  { key: "gross_profit", label: "Gross Profit", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: true },
                  { key: "gp_pct", label: "GP %", fmt: (v) => `${v}%`, positiveBetter: true },
                ]
              : [
                  { key: "billed", label: "Billed", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: true },
                  { key: "payments_received", label: "Collected", fmt: (v) => `₹${Math.round(v).toLocaleString()}`, positiveBetter: true },
                  { key: "collection_pct", label: "Collection %", fmt: (v) => `${v}%`, positiveBetter: true },
                ];

            // For each column compute the maximum across months (used for delta-vs-highest)
            const maxByCol = {};
            cols.forEach(c => { maxByCol[c.key] = Math.max(0, ...months.map(m => m[c.key] || 0)); });

            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-trend-table">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-100 z-10">Month</th>
                      {cols.map(c => (
                        <th key={c.key} className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((row, idx) => {
                      const isEmpty = cols.every(c => !row[c.key]);
                      const prev = idx > 0 ? months[idx - 1] : null;
                      return (
                        <tr key={idx} className={`border-b border-slate-100 ${isEmpty ? 'opacity-40' : 'hover:bg-blue-50'}`}>
                          <td className="px-3 py-2 font-semibold text-slate-700 sticky left-0 bg-white z-10 border-r border-slate-100">
                            {row.month_label}
                          </td>
                          {cols.map(c => {
                            const val = row[c.key] || 0;
                            const max = maxByCol[c.key] || 0;
                            const isMax = max > 0 && val === max;
                            // Compute delta vs the chosen baseline
                            let baseline, label;
                            if (trendCompareMode === "mom") {
                              baseline = prev ? (prev[c.key] || 0) : 0;
                              label = "vs prev";
                            } else {
                              baseline = max;
                              label = "vs peak";
                            }
                            const diff = val - baseline;
                            const pct = baseline ? (diff / baseline) * 100 : 0;
                            const showDelta = !isMax && baseline > 0;
                            const isUp = diff > 0;
                            const cellClass = trendCompareMode === "mom"
                              ? (diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : "text-slate-500")
                              : "text-red-600";
                            return (
                              <td key={c.key} className="px-3 py-2 text-right whitespace-nowrap">
                                <div className={`font-semibold ${isMax && trendCompareMode === "peak" ? 'text-emerald-700' : 'text-slate-800'}`}>
                                  {c.fmt(val)}
                                  {isMax && trendCompareMode === "peak" && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold align-middle">PEAK</span>}
                                </div>
                                {showDelta && (
                                  <div className={`text-[11px] font-medium ${cellClass}`}>
                                    {trendCompareMode === "mom" && (isUp ? "▲ " : diff < 0 ? "▼ " : "")}
                                    {c.fmt(Math.abs(diff))}{" "}
                                    <span className="text-slate-500">({pct.toFixed(1)}% {label})</span>
                                  </div>
                                )}
                                {!showDelta && trendCompareMode === "mom" && idx === 0 && val > 0 && (
                                  <div className="text-[11px] text-slate-400 italic">no prior month</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    {trendData?.totals && (
                      <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
                        <td className="px-3 py-2 sticky left-0 bg-slate-50 z-10 border-r border-slate-200">Total / Avg</td>
                        {cols.map(c => {
                          let v = trendData.totals[c.key] || 0;
                          // Percentage cols get displayed as the year-level avg (already returned by backend)
                          return (
                            <td key={c.key} className="px-3 py-2 text-right text-slate-800">
                              {c.fmt(v)}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-4">
                  <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 align-middle mr-1.5"></span>PEAK = best month for that metric</span>
                  {trendCompareMode === "peak"
                    ? <span>Red figures show how much each month falls short of the peak (and the % gap)</span>
                    : <span>▲ green = grew vs previous month, ▼ red = fell vs previous month</span>}
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Export Trend Dialog */}
      <Dialog open={trendShowExportDialog} onOpenChange={setTrendShowExportDialog}>
        <DialogContent className="max-w-md" data-testid="trend-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-emerald-600" /> Export Monthly Trend</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Pick which sheets to include in the workbook. Each sheet uses the same year ({trendYear}).</p>
            {[
              { key: "sales", label: "Sales (Parcels & Revenue)", desc: "Orders, Quantity, Revenue" },
              { key: "gp", label: "Gross Profit", desc: "Revenue, Cost, GP, GP %" },
              { key: "payments", label: "Payment Collection", desc: "Billed, Collected, Collection %" },
            ].map(o => (
              <label key={o.key} className="flex items-start gap-3 p-2.5 rounded border border-slate-200 hover:border-blue-300 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4"
                  checked={!!trendExportPicks[o.key]}
                  onChange={(e) => setTrendExportPicks(prev => ({ ...prev, [o.key]: e.target.checked }))}
                  data-testid={`trend-export-pick-${o.key}`}
                />
                <div>
                  <div className="font-semibold text-sm text-slate-700">{o.label}</div>
                  <div className="text-xs text-slate-500">{o.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrendShowExportDialog(false)} data-testid="trend-export-cancel">
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!Object.values(trendExportPicks).some(Boolean) || !trendData}
              data-testid="trend-export-confirm"
              onClick={() => {
                const months = trendData?.months || [];
                const totals = trendData?.totals || {};
                const buildSheet = (cols) => {
                  // Compute max per col for vs-Peak delta
                  const maxByCol = {};
                  cols.forEach(c => { maxByCol[c.key] = Math.max(0, ...months.map(m => m[c.key] || 0)); });
                  const headerCols = [
                    "Month",
                    ...cols.flatMap(c => [
                      c.label,
                      `${c.label} Δ vs Peak`,
                      `${c.label} Δ vs Peak %`,
                      `${c.label} Δ vs Prev Month`,
                      `${c.label} Δ vs Prev Month %`,
                    ])
                  ];
                  const rows = months.map((m, idx) => {
                    const r = [m.month_label];
                    const prev = idx > 0 ? months[idx - 1] : null;
                    cols.forEach(c => {
                      const v = m[c.key] || 0;
                      const max = maxByCol[c.key] || 0;
                      const peakDiff = v - max;
                      const peakPct = max > 0 ? (peakDiff / max) * 100 : 0;
                      const prevVal = prev ? (prev[c.key] || 0) : 0;
                      const momDiff = prev ? v - prevVal : "";
                      const momPct = prev && prevVal > 0 ? ((v - prevVal) / prevVal) * 100 : (prev ? "" : "");
                      r.push(v);
                      r.push(peakDiff);
                      r.push(Number(peakPct.toFixed(2)));
                      r.push(momDiff);
                      r.push(typeof momPct === "number" ? Number(momPct.toFixed(2)) : momPct);
                    });
                    return r;
                  });
                  const totalsRow = ["TOTAL / AVG"];
                  cols.forEach(c => {
                    totalsRow.push(totals[c.key] || 0);
                    totalsRow.push("");
                    totalsRow.push("");
                    totalsRow.push("");
                    totalsRow.push("");
                  });
                  return [headerCols, ...rows, totalsRow];
                };

                const wb = XLSX.utils.book_new();
                if (trendExportPicks.sales) {
                  const cols = [
                    { key: "orders", label: "Parcels" },
                    { key: "quantity", label: "Quantity" },
                    { key: "revenue", label: "Revenue (₹)" },
                  ];
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildSheet(cols)), "Sales");
                }
                if (trendExportPicks.gp) {
                  const cols = [
                    { key: "revenue", label: "Revenue (₹)" },
                    { key: "cost", label: "Cost (₹)" },
                    { key: "gross_profit", label: "Gross Profit (₹)" },
                    { key: "gp_pct", label: "GP %" },
                  ];
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildSheet(cols)), "Gross Profit");
                }
                if (trendExportPicks.payments) {
                  const cols = [
                    { key: "billed", label: "Billed (₹)" },
                    { key: "payments_received", label: "Collected (₹)" },
                    { key: "collection_pct", label: "Collection %" },
                  ];
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildSheet(cols)), "Payments");
                }
                const scopeLabel = trendScope === "company"
                  ? "company"
                  : (trendData?.scope?.manager_code ? `mgr_${trendData.scope.manager_code}` : trendData?.scope?.client_code || "filtered");
                XLSX.writeFile(wb, `monthly_trend_${trendYear}_${scopeLabel}.xlsx`);
                setTrendShowExportDialog(false);
                toast.success("Monthly trend workbook downloaded");
              }}
            >
              <Download className="w-4 h-4 mr-1.5" /> Download .xlsx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl print:hidden">
          <TabsTrigger value="partner" className="flex items-center gap-2" data-testid="tab-partner">
            <Building2 className="w-4 h-4" /> Partner
          </TabsTrigger>
          <TabsTrigger value="dpClient" className="flex items-center gap-2" data-testid="tab-dpclient">
            <Layers className="w-4 h-4" /> DP Client
          </TabsTrigger>
          <TabsTrigger value="agent" className="flex items-center gap-2" data-testid="tab-agent">
            <UserCheck className="w-4 h-4" /> Agent
          </TabsTrigger>
          <TabsTrigger value="manager" className="flex items-center gap-2" data-testid="tab-manager">
            <Users className="w-4 h-4" /> Manager
          </TabsTrigger>
        </TabsList>

        {/* Report Content (same for all tabs) */}
        <TabsContent value={activeTab} className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : reportData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
                <Card className={`bg-gradient-to-br ${getLevelColor(activeTab)} text-white border-0`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/70 text-sm">Total Orders</p>
                        <p className="text-2xl font-bold">{reportData.summary?.total_orders?.toLocaleString() || 0}</p>
                        {comparisonData && (
                          <div className="flex items-center gap-1 mt-1">
                            {calcChange(reportData.summary?.total_orders, comparisonData.summary?.total_orders) > 0 ? (
                              <ArrowUpRight className="w-3 h-3 text-green-300" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3 text-red-300" />
                            )}
                            <span className="text-xs text-white/80">
                              {calcChange(reportData.summary?.total_orders, comparisonData.summary?.total_orders)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <Package className="w-10 h-10 text-white/30" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/70 text-sm">Total Revenue</p>
                        <p className="text-2xl font-bold">{formatCurrency(reportData.summary?.total_revenue)}</p>
                        {comparisonData && (
                          <div className="flex items-center gap-1 mt-1">
                            {calcChange(reportData.summary?.total_revenue, comparisonData.summary?.total_revenue) > 0 ? (
                              <ArrowUpRight className="w-3 h-3 text-green-300" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3 text-red-300" />
                            )}
                            <span className="text-xs text-white/80">
                              {calcChange(reportData.summary?.total_revenue, comparisonData.summary?.total_revenue)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <DollarSign className="w-10 h-10 text-white/30" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/70 text-sm">Gross Profit</p>
                        <p className="text-2xl font-bold">{formatCurrency(reportData.summary?.total_gp)}</p>
                        {comparisonData && (
                          <div className="flex items-center gap-1 mt-1">
                            {calcChange(reportData.summary?.total_gp, comparisonData.summary?.total_gp) > 0 ? (
                              <ArrowUpRight className="w-3 h-3 text-green-300" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3 text-red-300" />
                            )}
                            <span className="text-xs text-white/80">
                              {calcChange(reportData.summary?.total_gp, comparisonData.summary?.total_gp)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <TrendingUp className="w-10 h-10 text-white/30" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/70 text-sm">Avg GP %</p>
                        <p className="text-2xl font-bold">{reportData.summary?.avg_gp_percentage?.toFixed(1) || 0}%</p>
                        {comparisonData && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-white/80">
                              vs {comparisonData.summary?.avg_gp_percentage?.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <Percent className="w-10 h-10 text-white/30" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 print:grid-cols-2">
                {/* Revenue Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Revenue by {activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager"}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        {chartType === "bar" ? (
                          <BarChart data={reportData.items?.slice(0, 10) || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="revenue" name="Revenue" fill={LEVEL_COLORS[activeTab][0]} />
                            {comparisonData && (
                              <Bar dataKey="prev_revenue" name="Prev Revenue" fill={LEVEL_COLORS[activeTab][2]} />
                            )}
                          </BarChart>
                        ) : chartType === "line" ? (
                          <LineChart data={reportData.items?.slice(0, 10) || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="revenue" name="Revenue" stroke={LEVEL_COLORS[activeTab][0]} strokeWidth={2} />
                          </LineChart>
                        ) : (
                          <PieChart>
                            <Pie
                              data={reportData.items?.slice(0, 7) || []}
                              dataKey="revenue"
                              nameKey="code"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ code, percent }) => `${code} (${(percent * 100).toFixed(0)}%)`}
                            >
                              {reportData.items?.slice(0, 7).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* GP Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Gross Profit by {activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager"}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData.items?.slice(0, 10) || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="code" tick={{ fontSize: 11 }} width={60} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="gross_profit" name="Gross Profit" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Data Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <LevelIcon className="w-5 h-5" />
                    {activeTab === "partner" ? "Partner" : activeTab === "dpClient" ? "DP Client" : activeTab === "agent" ? "Agent" : "Manager"} Performance Details
                    <Badge variant="outline" className="ml-2">{reportData.items?.length || 0} items</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="performance-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium">Code</th>
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-right p-3 font-medium">Orders</th>
                          <th className="text-right p-3 font-medium">Quantity</th>
                          <th className="text-right p-3 font-medium">Revenue</th>
                          <th className="text-right p-3 font-medium">Cost</th>
                          <th className="text-right p-3 font-medium">GP</th>
                          <th className="text-center p-3 font-medium">GP %</th>
                          {comparisonData && (
                            <th className="text-center p-3 font-medium">Change</th>
                          )}
                          <th className="text-center p-3 font-medium print:hidden">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.items?.map((item, idx) => {
                          const prevItem = comparisonData?.items?.find(c => c.code === item.code);
                          const revenueChange = calcChange(item.revenue, prevItem?.revenue);
                          const isExpanded = expandedItems[item.code];
                          
                          return (
                            <>
                              <tr key={item.code} className={`border-b hover:bg-slate-50 ${isExpanded ? 'bg-blue-50' : ''}`}>
                                <td className="p-3 font-medium">{item.code}</td>
                                <td className="p-3">{item.name || "-"}</td>
                                <td className="p-3 text-right">{item.orders?.toLocaleString()}</td>
                                <td className="p-3 text-right">{item.quantity?.toLocaleString()}</td>
                                <td className="p-3 text-right font-medium">{formatCurrency(item.revenue)}</td>
                                <td className="p-3 text-right">{formatCurrency(item.cost)}</td>
                                <td className="p-3 text-right font-medium text-green-600">{formatCurrency(item.gross_profit)}</td>
                                <td className="p-3 text-center">
                                  <Badge className={`${item.gp_percentage >= 20 ? 'bg-green-100 text-green-700' : item.gp_percentage >= 10 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                    {item.gp_percentage?.toFixed(1)}%
                                  </Badge>
                                </td>
                                {comparisonData && (
                                  <td className="p-3 text-center">
                                    {revenueChange !== null && (
                                      <Badge className={`${Number(revenueChange) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {Number(revenueChange) > 0 ? '+' : ''}{revenueChange}%
                                      </Badge>
                                    )}
                                  </td>
                                )}
                                <td className="p-3 text-center print:hidden">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleItem(item.code)}
                                  >
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </Button>
                                </td>
                              </tr>
                              
                              {/* Drill-down Row */}
                              {isExpanded && (
                                <tr id={`drilldown-${item.code}`}>
                                  <td colSpan={comparisonData ? 10 : 9} className="p-0">
                                    <div className="bg-slate-50 p-4 space-y-4">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <Card className="bg-white">
                                          <CardContent className="p-3">
                                            <p className="text-xs text-slate-500">Avg Order Value</p>
                                            <p className="text-lg font-bold">{formatCurrency(item.avg_order_value)}</p>
                                          </CardContent>
                                        </Card>
                                        <Card className="bg-white">
                                          <CardContent className="p-3">
                                            <p className="text-xs text-slate-500">Avg GP per Order</p>
                                            <p className="text-lg font-bold">{formatCurrency(item.gross_profit / (item.orders || 1))}</p>
                                          </CardContent>
                                        </Card>
                                        <Card className="bg-white">
                                          <CardContent className="p-3">
                                            <p className="text-xs text-slate-500">Avg Qty per Order</p>
                                            <p className="text-lg font-bold">{(item.quantity / (item.orders || 1)).toFixed(1)}</p>
                                          </CardContent>
                                        </Card>
                                        <Card className="bg-white">
                                          <CardContent className="p-3">
                                            <p className="text-xs text-slate-500">Cost per Unit</p>
                                            <p className="text-lg font-bold">{formatCurrency(item.cost / (item.quantity || 1))}</p>
                                          </CardContent>
                                        </Card>
                                      </div>
                                      
                                      {/* Top Products */}
                                      {item.top_products?.length > 0 && (
                                        <div>
                                          <h4 className="font-medium mb-2 text-sm">Top Products</h4>
                                          <div className="flex flex-wrap gap-2">
                                            {item.top_products.slice(0, 5).map((p, i) => (
                                              <Badge key={i} variant="outline" className="text-xs">
                                                {p.name} ({p.quantity})
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Monthly Trend */}
                                      {item.monthly_trend?.length > 0 && (
                                        <div>
                                          <h4 className="font-medium mb-2 text-sm">Monthly Trend</h4>
                                          <div className="h-32">
                                            <ResponsiveContainer width="100%" height="100%">
                                              <AreaChart data={item.monthly_trend}>
                                                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                                <YAxis tick={{ fontSize: 10 }} />
                                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                                <Area type="monotone" dataKey="revenue" stroke={LEVEL_COLORS[activeTab][0]} fill={LEVEL_COLORS[activeTab][3]} />
                                              </AreaChart>
                                            </ResponsiveContainer>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* ────────── MONTHLY MATRIX (all rows × 12 months) ────────── */}
              <Card data-testid="monthly-matrix-card">
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-500" />
                        Monthly Matrix — {selectedYear} · {activeTab === "partner" ? "Partners" : activeTab === "dpClient" ? "DP Clients" : activeTab === "agent" ? "Agents" : "Managers"}
                      </h3>
                      <p className="text-xs text-muted-foreground">All rows × 12 months. Grand totals in the last column.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex gap-1 rounded-lg border p-0.5 bg-muted/40" role="tablist" data-testid="matrix-metric-tabs">
                        {[
                          { k: "orders", label: "Orders" },
                          { k: "revenue", label: "Revenue" },
                          { k: "gp", label: "GP" },
                        ].map((m) => (
                          <button
                            key={m.k}
                            type="button"
                            onClick={() => setMatrixMetric(m.k)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${matrixMetric === m.k ? "bg-white shadow text-indigo-600 dark:bg-slate-800" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`matrix-metric-${m.k}`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setMatrixComparePrev((v) => !v)}
                        className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition ${matrixComparePrev ? "bg-indigo-600 border-indigo-700 text-white" : "bg-background text-foreground hover:bg-muted"}`}
                        data-testid="matrix-compare-toggle"
                        title={`Show delta vs ${selectedYear - 1}`}
                      >
                        vs {selectedYear - 1}
                      </button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 h-8"
                        onClick={exportMatrixExcel}
                        disabled={!matrixData || (matrixData.rows || []).length === 0}
                        data-testid="matrix-export-btn"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                      </Button>
                    </div>
                  </div>

                  {matrixLoading ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading matrix…
                    </div>
                  ) : !matrixData || (matrixData.rows || []).length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                      No data for {selectedYear}
                    </div>
                  ) : (
                    <>
                    {matrixTopMovers && (matrixTopMovers.gainers.length > 0 || matrixTopMovers.droppers.length > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="top-movers-strip">
                        <MoverCard
                          title="Top Growers"
                          data={matrixTopMovers.gainers}
                          metric={matrixMetric}
                          selectedYear={selectedYear}
                          positive
                        />
                        <MoverCard
                          title="Top Droppers"
                          data={matrixTopMovers.droppers}
                          metric={matrixMetric}
                          selectedYear={selectedYear}
                          positive={false}
                        />
                      </div>
                    )}
                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full text-xs sm:text-sm border-collapse" data-testid="matrix-table">
                        <thead className="sticky top-0 bg-muted/60 z-10">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold sticky left-0 bg-muted/60 z-20 min-w-[140px]">Client</th>
                            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, idx) => (
                              <th key={idx} className="px-2 py-1.5 text-right font-semibold">{m}</th>
                            ))}
                            <th className="px-2 py-1.5 text-right font-bold bg-indigo-100 dark:bg-indigo-950">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matrixData.rows.map((r) => {
                            const prev = prevByCode[r.code];
                            return (
                            <tr key={r.code} className="border-t hover:bg-muted/30" data-testid={`matrix-row-${r.code}`}>
                              <td className="px-2 py-1 font-medium sticky left-0 bg-background z-10">
                                <div className="truncate max-w-[180px]" title={r.name}>{r.name || r.code}</div>
                                {r.name && r.name.toUpperCase() !== r.code.toUpperCase() && (
                                  <div className="text-[10px] text-muted-foreground font-mono">{r.code}</div>
                                )}
                              </td>
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => {
                                const cell = r.months[m] || r.months[String(m)] || { orders: 0, revenue: 0, gp: 0 };
                                const val = cell[matrixMetric];
                                const isZero = !val;
                                const isNeg = matrixMetric === "gp" && val < 0;
                                let deltaText = null;
                                if (matrixComparePrev && prev) {
                                  const pcell = prev.months[m] || prev.months[String(m)] || { orders: 0, revenue: 0, gp: 0 };
                                  const pv = pcell[matrixMetric];
                                  if (val !== 0 || pv !== 0) {
                                    const d = deltaPct(val, pv);
                                    const up = d >= 0;
                                    deltaText = (
                                      <span className={`text-[9px] font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                                        {up ? "▲" : "▼"} {Math.abs(d).toFixed(d >= 100 || d <= -100 ? 0 : 1)}%
                                      </span>
                                    );
                                  }
                                }
                                return (
                                  <td key={m} className={`px-2 py-1 text-right font-mono ${isZero ? "text-muted-foreground/60" : isNeg ? "text-rose-600" : ""}`}>
                                    <div>
                                      {matrixMetric === "orders"
                                        ? (val || 0).toLocaleString()
                                        : val ? formatCurrency(val) : "—"}
                                    </div>
                                    {deltaText}
                                  </td>
                                );
                              })}
                              <td className="px-2 py-1 text-right font-bold bg-indigo-50 dark:bg-indigo-950/40">
                                <div>
                                  {matrixMetric === "orders"
                                    ? (r.total.orders || 0).toLocaleString()
                                    : formatCurrency(r.total[matrixMetric] || 0)}
                                </div>
                                {matrixComparePrev && prev && (() => {
                                  const pv = prev.total?.[matrixMetric] || 0;
                                  const cv = r.total?.[matrixMetric] || 0;
                                  if (pv === 0 && cv === 0) return null;
                                  const d = deltaPct(cv, pv);
                                  const up = d >= 0;
                                  return (
                                    <span className={`text-[9px] font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                                      {up ? "▲" : "▼"} {Math.abs(d).toFixed(d >= 100 || d <= -100 ? 0 : 1)}%
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                            );
                          })}
                          {/* Grand total row */}
                          <tr className="border-t-2 border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30 font-bold">
                            <td className="px-2 py-1.5 sticky left-0 bg-indigo-50/60 dark:bg-indigo-950/30">TOTAL</td>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => {
                              const gm = matrixData.grand_total?.months?.[m] || matrixData.grand_total?.months?.[String(m)] || { orders: 0, revenue: 0, gp: 0 };
                              const val = gm[matrixMetric];
                              return (
                                <td key={m} className="px-2 py-1.5 text-right font-mono">
                                  {matrixMetric === "orders"
                                    ? (val || 0).toLocaleString()
                                    : val ? formatCurrency(val) : "—"}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-right bg-indigo-100 dark:bg-indigo-900">
                              {matrixMetric === "orders"
                                ? (matrixData.grand_total?.orders || 0).toLocaleString()
                                : formatCurrency(matrixData.grand_total?.[matrixMetric] || 0)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No data available for selected period</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PerformanceReportsPage;

// Small card used by the Monthly Matrix's Top Movers strip.
const MoverCard = ({ title, data, metric, selectedYear, positive }) => {
  const isCurrency = metric !== "orders";
  const fmt = (n) => (isCurrency
    ? `₹${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : Math.abs(n).toLocaleString());
  const wrapperCls = positive
    ? "rounded-lg border p-2.5 bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-800"
    : "rounded-lg border p-2.5 bg-rose-50/50 dark:bg-rose-950/10 border-rose-200 dark:border-rose-800";
  const titleCls = positive
    ? "text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
    : "text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-400";
  const badgeCls = positive
    ? "inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex-shrink-0"
    : "inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-600 text-white text-[9px] font-bold flex-shrink-0";
  const deltaCls = positive
    ? "font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap"
    : "font-semibold text-rose-600 dark:text-rose-400 whitespace-nowrap";
  return (
    <div className={wrapperCls} data-testid={`mover-card-${positive ? "growers" : "droppers"}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={titleCls}>
          {positive ? "▲" : "▼"} {title}
        </span>
        <span className="text-[10px] text-muted-foreground">vs {selectedYear - 1}</span>
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No movers</p>
      ) : (
        <ol className="space-y-1">
          {data.map((m, i) => (
            <li key={m.code} className="flex items-center gap-2 text-xs" data-testid={`mover-row-${positive ? "growers" : "droppers"}-${i}`}>
              <span className={badgeCls}>{i + 1}</span>
              <span className="font-medium truncate flex-1" title={m.name}>{m.name}</span>
              <span className="font-mono text-muted-foreground">{fmt(m.cur)}</span>
              <span className={deltaCls}>
                {positive ? "+" : ""}{fmt(m.deltaAbs)}
                <span className="opacity-75 ml-0.5">({m.deltaPct >= 0 ? "+" : ""}{m.deltaPct.toFixed(0)}%)</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
